import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./iframe.css";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import MatchOddsSection from "../common/MatchOddsSection";
import ComboOrWinSection from "../common/ComboOrWinSection";
import FancyDetailRow from "../common/FancyDetailRow";
import PlaceBetOpenBets from "../common/PlaceBetOpenBets";
import { isAuthenticated } from "../../utils/auth";
import {
  getSportDetailForMarketODDSAndFencyMarketInterval,
  openBetsFetchInterval,
  fancyLiabilityAndTeamPositionFetchInterval,
} from "../../env";
import {
  fetchMatchDetailsAsync,
  fetchFanciesAsync,
  fetchFancyLiabilityAsync,
  fetchTeamPositionAsync,
  fetchTvUrlAsync,
  clearGameData,
} from "../../store/slices/gameSlice";
import {
  fetchOpenBetsAsync,
  fetchUnmatchedBets,
} from "../../store/slices/betSlice";
import { fetchMultiMarketDetails } from "../../store/slices/multiMarketSlice";

const useShortPolling = (callback, intervalMs, enabled = true) => {
  useEffect(() => {
    if (!enabled || typeof intervalMs !== "number" || intervalMs <= 0) return;

    const id = setInterval(() => {
      callback();
    }, intervalMs);

    return () => clearInterval(id);
  }, [callback, intervalMs, enabled]);
};

const SportDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const allMatches = useSelector((state) => state.matches.matches);
  const authUser = useSelector((state) => state.auth?.user);
  const {
    matchDetails,
    fancies,
    fancyCategories,
    fancyLiability,
    teamPosition,
    tvUrl,
    loading: matchLoading,
  } = useSelector((state) => state.game);
  const selectedBet = useSelector((state) => state.game.selectedBet);
  const favoriteMarkets = useSelector((state) => state.multiMarket?.markets);

  const isLoggedIn = typeof window !== "undefined" ? isAuthenticated() : false;

  const matchId = routeId ? String(routeId) : null;

  const marketId = useMemo(() => {
    if (!matchId || !Array.isArray(allMatches) || allMatches.length === 0) {
      return null;
    }

    const found = allMatches.find((m) => {
      const candidateId = m?.match_id ?? m?.event_id ?? m?.id ?? m?._id ?? null;
      return candidateId != null && String(candidateId) === matchId;
    });

    return found?.market_id ?? found?.marketId ?? null;
  }, [allMatches, matchId]);

  const sportId = useMemo(() => {
    if (!matchId || !Array.isArray(allMatches) || allMatches.length === 0) {
      return null;
    }

    const found = allMatches.find((m) => {
      const candidateId = m?.match_id ?? m?.event_id ?? m?.id ?? m?._id ?? null;
      return candidateId != null && String(candidateId) === matchId;
    });

    return found?.sport_id ?? found?.sportId ?? null;
  }, [allMatches, matchId]);

  useEffect(() => {
    if (!matchId || !isLoggedIn) return;
    // Clear previous match data and selected bet so UI does not
    // briefly show stale details or an open betslip for the last match.
    dispatch(clearGameData());
  }, [dispatch, matchId, isLoggedIn]);

  useEffect(() => {
    if (!matchId || !isLoggedIn) return;

    dispatch(fetchMatchDetailsAsync({ matchId }));
    dispatch(fetchFanciesAsync({ matchId }));
    dispatch(fetchTeamPositionAsync({ matchId })).then((action) => {
      // eslint-disable-next-line no-console
    });
    dispatch(fetchFancyLiabilityAsync({ matchId })).then((action) => {
      // eslint-disable-next-line no-console
    });
    dispatch(fetchOpenBetsAsync()).then((action) => {
      // eslint-disable-next-line no-console
    });
    const effectiveSportId = sportId;
    const eventId =
      Number(effectiveSportId) === 7 || Number(effectiveSportId) === 4339
        ? marketId
        : matchId;
    if (eventId && effectiveSportId) {
      dispatch(fetchUnmatchedBets({ eventId, sportId: effectiveSportId })).then(
        (action) => {
          // eslint-disable-next-line no-console
        },
      );
    }
  }, [dispatch, matchId, marketId, sportId, isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn) return;
    dispatch(fetchMultiMarketDetails());
  }, [dispatch, isLoggedIn]);

  const favoriteMarketKeySet = useMemo(() => {
    const set = new Set();
    for (const market of Array.isArray(favoriteMarkets)
      ? favoriteMarkets
      : []) {
      const favMatchId = market?.match_id ?? market?.matchId ?? null;
      const favMarketId =
        market?.market_id ?? market?.marketId ?? market?.id ?? null;
      if (favMatchId == null || favMarketId == null) continue;
      set.add(`${String(favMatchId)}::${String(favMarketId)}`);
    }
    return set;
  }, [favoriteMarkets]);

  const isMarkedFavorite = useCallback(
    (candidateMatchId, candidateMarketId) => {
      if (candidateMatchId == null || candidateMarketId == null) return false;
      return favoriteMarketKeySet.has(
        `${String(candidateMatchId)}::${String(candidateMarketId)}`,
      );
    },
    [favoriteMarketKeySet],
  );

  const pollMatchAndFancies = useCallback(() => {
    if (!matchId || !isLoggedIn) return;
    dispatch(fetchMatchDetailsAsync({ matchId }));
    dispatch(fetchFanciesAsync({ matchId }));
  }, [dispatch, matchId, marketId, isLoggedIn]);

  const pollOpenBets = useCallback(() => {
    if (!matchId || !isLoggedIn) return;

    dispatch(fetchOpenBetsAsync()).then((action) => {
      // eslint-disable-next-line no-console
    });
  }, [dispatch, matchId, isLoggedIn]);

  const pollTeamAndFancyLiability = useCallback(() => {
    if (!matchId || !isLoggedIn) return;

    dispatch(fetchTeamPositionAsync({ matchId })).then((tpAction) => {
      // eslint-disable-next-line no-console
    });
    dispatch(fetchFancyLiabilityAsync({ matchId })).then((flAction) => {
      // eslint-disable-next-line no-console
    });
    const effectiveSportId = sportId;
    const eventId =
      Number(effectiveSportId) === 7 || Number(effectiveSportId) === 4339
        ? marketId
        : matchId;
    if (eventId && effectiveSportId) {
      dispatch(fetchUnmatchedBets({ eventId, sportId: effectiveSportId })).then(
        (unmatchedAction) => {
          // eslint-disable-next-line no-console
        },
      );
    }
  }, [dispatch, matchId, marketId, sportId, isLoggedIn]);

  useShortPolling(
    pollMatchAndFancies,
    getSportDetailForMarketODDSAndFencyMarketInterval,
    !!matchId && isLoggedIn,
  );
  useShortPolling(pollOpenBets, openBetsFetchInterval, !!matchId && isLoggedIn);
  useShortPolling(
    pollTeamAndFancyLiability,
    fancyLiabilityAndTeamPositionFetchInterval,
    !!matchId && isLoggedIn,
  );

  // Helper to normalize market runners from API into MatchOddsSection format
  const buildRunnersFromMarket = (market) => {
    if (!market || !Array.isArray(market.runners)) return [];

    const volumeRaw = market?.matched ?? market?.totalMatched;
    const volumeText =
      volumeRaw != null && volumeRaw !== "" ? String(volumeRaw) : null;

    const marketKey =
      market?.market_id ?? market?.id ?? market?.marketId ?? null;

    let positionsForMarket = [];
    if (teamPosition && typeof teamPosition === "object" && marketKey != null) {
      const byMarket =
        teamPosition[marketKey] || teamPosition[String(marketKey)];
      if (Array.isArray(byMarket)) {
        positionsForMarket = byMarket;
      }
    }

    return market.runners.map((runner) => {
      const marketStatus = String(market?.status || "").toUpperCase();
      const runnerStatus = String(runner?.status || "").toUpperCase();
      const isSuspended =
        marketStatus === "SUSPENDED" || runnerStatus === "SUSPENDED";

      const selectionId =
        runner?.selectionId ??
        runner?.selection_id ??
        runner?.id ??
        runner?._id ??
        null;

      const position =
        positionsForMarket.find(
          (p) =>
            String(p?.selection_id) === String(selectionId) ||
            String(p?.selectionId) === String(selectionId),
        ) || null;
      const userPlRaw = position?.user_pl;

      const backs = runner?.ex?.availableToBack || [];
      const lays = runner?.ex?.availableToLay || [];

      const ensureThree = (arr) => {
        const prices = ["-", "-", "-"];
        const sizes = ["0.0", "0.0", "0.0"];

        arr.slice(0, 3).forEach((q, idx) => {
          prices[idx] = q?.price != null ? String(q.price) : "-";
          sizes[idx] = q?.size != null ? String(q.size) : "0.0";
        });

        return { prices, sizes };
      };

      const { prices: backPrices, sizes: backSizes } = ensureThree(backs);
      const { prices: layPrices, sizes: laySizes } = ensureThree(lays);

      return {
        name: runner?.selection_name || runner?.name || "",
        selectionId,
        marketId: marketKey,
        volumeText,
        volumeClass: "",
        isSuspended,
        backPrices,
        backSizes,
        layPrices,
        laySizes,
        // Only set a numeric P/L when we actually have a position record
        // for this runner. If there is no record, keep it as null so the
        // UI can distinguish "no bets" from "bets that net to 0.00".
        userPl:
          position == null
            ? null
            : typeof userPlRaw === "number"
              ? userPlRaw
              : userPlRaw != null
                ? Number(userPlRaw) || 0
                : 0,
      };
    });
  };

  const matchDetailsArray = useMemo(
    () => (Array.isArray(matchDetails) ? matchDetails : []),
    [matchDetails],
  );

  const isDemoUser = !!authUser?.isDemo;
  // Used by fancy min/max stake display. Keep legacy behavior (first market).
  const isInplay = matchDetailsArray[0]?.inplay ?? false;
  const normalizeMarketStatus = (v) =>
    String(v ?? "")
      .toUpperCase()
      .trim()
      .replace(/[- ]/g, "_");
  const isInPlayMarket = (item) => {
    if (!item) return false;
    if (item?.inplay === true) return true;
    const status = normalizeMarketStatus(item?.status);
    return status === "IN_PLAY";
  };
  const isInplayMatch = useMemo(() => {
    if (!Array.isArray(matchDetailsArray) || matchDetailsArray.length === 0)
      return false;
    return matchDetailsArray.some(
      (m) => Boolean(m?.inplay) || isInPlayMarket(m),
    );
  }, [matchDetailsArray]);

  useEffect(() => {
    if (!matchId || !isLoggedIn) return;
    dispatch(fetchTvUrlAsync(matchId));
  }, [dispatch, matchId, isLoggedIn]);

  const matchInfo = matchDetailsArray[0] || {};

  const formatDateTime = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleString([], {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    } catch {
      return "";
    }
  };

  const headerTitle =
    matchInfo.match_name || matchInfo.event_name || matchInfo.name || "Match";
  const headerDateTime = formatDateTime(matchInfo.match_date);

  const normalizeMarketType = (val) =>
    String(val || "")
      .toUpperCase()
      .replace(/\s+/g, "_");

  const getMarketMinMaxText = (market) => {
    if (!market) return "Min: - | Max: -";
    const minStake = market?.inplay
      ? market?.market_min_stack
      : (market?.market_advance_bet_min_stake ?? null);
    const maxStake = market?.inplay
      ? market?.market_max_stack
      : (market?.market_advance_bet_stake ?? null);
    return `Min: ${minStake != null ? minStake : "-"} | Max: ${
      maxStake != null ? maxStake : "-"
    }`;
  };

  const hasData = matchDetailsArray.length > 0;

  const tvPayload = tvUrl?.data ?? tvUrl ?? {};
  const isRealUser = !isDemoUser;
  const matchTvUrl = isRealUser && isInplayMatch ? tvPayload?.tv_url : null;
  const scoreboardUrl =
    isRealUser && isInplayMatch ? tvPayload?.match_scoreboard_url : null;

  const [tvOpen, setTvOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.innerWidth > 991;
  });
  const [scoreCardOpen, setScoreCardOpen] = useState(true);
  const [openBetsOpen, setOpenBetsOpen] = useState(false);
  const openOpenBetsModal = useCallback(() => {
    setOpenBetsOpen(true);
  }, []);

  const closeOpenBetsModal = useCallback(() => {
    setOpenBetsOpen(false);
  }, []);
  useEffect(() => {
    if (typeof window === "undefined") return;

    const syncTvOpenByScreen = () => {
      if (window.innerWidth <= 991) {
        setTvOpen(false);
      }
    };

    syncTvOpenByScreen();
    window.addEventListener("resize", syncTvOpenByScreen);
    return () => window.removeEventListener("resize", syncTvOpenByScreen);
  }, []);
  // ---------- Fancy markets (dynamic from API) ----------

  // --------- Fancy data & categories from Redux ---------
  const fancyData = useMemo(() => {
    if (Array.isArray(fancies)) {
      // Shape: game.fancies = [ { fancy_id, name, ... }, ... ]
      return fancies;
    }

    if (fancies && typeof fancies === "object") {
      // Shape: game.fancies = { data: [...], fancy_category: {...}, status: true }
      if (Array.isArray(fancies.data)) {
        return fancies.data;
      }

      // Fallback: sometimes wrapped inside first element
      if (Array.isArray(fancies[0]?.data)) {
        return fancies[0].data;
      }
    }

    return [];
  }, [fancies]);

  const effectiveFancyCategories =
    (fancyCategories &&
      typeof fancyCategories === "object" &&
      fancyCategories) ||
    (fancies &&
      !Array.isArray(fancies) &&
      typeof fancies === "object" &&
      fancies.fancy_category &&
      typeof fancies.fancy_category === "object" &&
      fancies.fancy_category) ||
    {};

  const ALL_TAB = "All";
  const [activeFancyTab, setActiveFancyTab] = useState(ALL_TAB);

  const FANCY_TYPES = [
    "Main Fancy",
    "Session Markets",
    "NORMAL",
    "Over by Over",
    "Ball by Ball",
    "Khado",
    "Meter",
    "oddeven",
    "Fancy1",
    "Fall Of Wicket",
    "Total Advance",
  ];

  const fancyTabs = useMemo(() => {
    const labels = [ALL_TAB];
    const seen = new Set(labels);

    const categories = Array.isArray(effectiveFancyCategories)
      ? effectiveFancyCategories
      : effectiveFancyCategories && typeof effectiveFancyCategories === "object"
        ? Object.values(effectiveFancyCategories)
        : [];

    const normalizedCategories = categories
      .map((categoryLabel) => String(categoryLabel || "").trim())
      .filter(Boolean);

    // Sort according to FANCY_TYPES
    const orderedCategories = normalizedCategories.sort((a, b) => {
      const aIndex = FANCY_TYPES.indexOf(a);
      const bIndex = FANCY_TYPES.indexOf(b);

      // Known categories follow FANCY_TYPES order
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }

      // Known categories come before unknown categories
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;

      // Keep unknown categories alphabetical
      return a.localeCompare(b);
    });

    orderedCategories.forEach((label) => {
      if (seen.has(label)) return;

      seen.add(label);
      labels.push(label);
    });

    return labels;
  }, [effectiveFancyCategories]);

  useEffect(() => {
    if (!fancyTabs.includes(activeFancyTab)) {
      setActiveFancyTab(ALL_TAB);
    }
  }, [activeFancyTab, fancyTabs]);

  const normalizedFancyItems = useMemo(
    () =>
      fancyData.map((item, index) => {
        const id = String(
          item?.fancy_id || item?.selection_id || `fancy-${index}`,
        );

        // Support multiple possible response shapes:
        // { "353...": -2 }, { data: { "353...": -2 } }, or [{ "353...": -2 }]
        let liabilityMap =
          fancyLiability && typeof fancyLiability === "object"
            ? fancyLiability
            : {};
        if (liabilityMap.data && typeof liabilityMap.data === "object") {
          liabilityMap = liabilityMap.data;
        } else if (Array.isArray(liabilityMap) && liabilityMap[0]) {
          liabilityMap = liabilityMap[0];
        }

        const rawLiability =
          liabilityMap[id] ??
          liabilityMap[String(item?.fancy_id)] ??
          liabilityMap[String(item?.selection_id)];

        const categoryKey =
          typeof item?.category === "number" ||
          typeof item?.category === "string"
            ? item.category
            : null;
        const categoryLabel =
          (categoryKey != null && effectiveFancyCategories?.[categoryKey]) ||
          "NORMAL";

        const lay = item?.LayPrice1 ?? "";
        const laySize = item?.LaySize1 ?? "";
        const back = item?.BackPrice1 ?? "";
        const backSize = item?.BackSize1 ?? "";

        const minStake = isInplay
          ? item?.session_min_stack
          : item?.session_before_inplay_min_stack;
        const maxStake = isInplay
          ? item?.session_max_stack
          : item?.session_before_inplay_max_stack;

        const minBetRaw =
          item?.session_min_stack ??
          item?.session_before_inplay_min_stack ??
          "";
        const maxBetRaw =
          item?.session_max_stack ??
          item?.session_before_inplay_max_stack ??
          "";

        const rawGameStatus =
          typeof item?.GameStatus === "string" ? item.GameStatus : "";
        const trimmedGameStatus = rawGameStatus.trim();
        const isSuspended =
          trimmedGameStatus !== "" &&
          trimmedGameStatus.toUpperCase() === "SUSPENDED";

        return {
          id,
          label: item?.name || item?.fancy_name || "",
          labelSuffix: "",
          fancyId: item?.fancy_id ?? null,
          selectionId: item?.selection_id ?? null,
          lay,
          laySize,
          back,
          minStake,
          maxStake,
          backSize,
          backOnly: lay === "",
          categoryLabel,
          gameStatus: trimmedGameStatus,
          isSuspended,
          liability:
            typeof rawLiability === "number"
              ? rawLiability
              : rawLiability != null
                ? Number(rawLiability) || 0
                : 0,
        };
      }),
    [fancyData, effectiveFancyCategories, fancyLiability],
  );

  const filteredFancyItems = useMemo(() => {
    if (activeFancyTab === ALL_TAB) return normalizedFancyItems;
    return normalizedFancyItems.filter((item) => {
      const tab = String(activeFancyTab).toLowerCase();
      const categoryMatch =
        String(item.categoryLabel || "").toLowerCase() === tab;
      const nameMatch = String(item.label || "").toLowerCase() === tab;
      return categoryMatch || nameMatch;
    });
  }, [activeFancyTab, normalizedFancyItems]);

  // Keep legacy components available without rendering them
  const legacyFancyAndCombo = false && (
    <>
      <ComboOrWinSection
        section={{
          type: "combo",
          title: "",
          collapseId: "legacy-combo",
          items: [],
        }}
      />
      <FancyDetailRow
        item={{
          id: "legacy-fancy",
          label: "",
          back: "",
          backSize: "",
          lay: "",
          laySize: "",
          maxBet: "",
          maxMarket: "",
        }}
      />
    </>
  );

  const dispatchSelectedBet = useCallback(
    (payload) => {
      dispatch({
        type: "game/setSelectedBet",
        payload,
      });
    },
    [dispatch],
  );

  if (!isLoggedIn) {
    return (
      <section className="section listing_page">
        <div className="row">
          <div className="col-md-12 col-lg-8 detail-center-column">
            <div className="middle-page-content details-page">
              <div className="card-header game-heading">
                <span className="card-header-title">
                  Please log in to view this page
                </span>
              </div>
              <div className="card-body py-4 text-center">
                <p className="mb-3">
                  You need to be logged in to view match details and place bets.
                </p>
                <button
                  type="button"
                  className="btn btn-login"
                  onClick={() => {
                    if (
                      typeof window !== "undefined" &&
                      typeof window.openLoginDialog === "function"
                    ) {
                      window.openLoginDialog();
                    } else {
                      navigate("/login");
                    }
                  }}
                >
                  Login
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="section listing_page">
      <div className="row">
        <div className="col-md-12 col-lg-8 detail-center-column">
          <div className="middle-page-content details-page">
            {matchLoading && (
              <div className="card-header game-heading">
                <span className="card-header-title">Loading…</span>
              </div>
            )}
            {!matchLoading && !hasData && (
              <div className="card-header game-heading">
                <span className="card-header-title">
                  No details currently available
                </span>
              </div>
            )}
            {!matchLoading && hasData && (
              <>
                <div className="card-header game-heading">
                  <span className="card-header-title">{headerTitle}</span>
                  {headerDateTime && (
                    <span className="date-time">({headerDateTime})</span>
                  )}
                  <Link
                    to="#"
                    className="best_link d-lg-none"
                    onClick={(e) => {
                      e.preventDefault?.();
                      e.stopPropagation?.();
                      openOpenBetsModal();
                    }}
                    aria-label="Open bets"
                  >
                    Bets
                  </Link>
                </div>
                {openBetsOpen ? (
                  <>
                    <div
                      className="modal-backdrop fade show"
                      onClick={closeOpenBetsModal}
                    />
                    <div
                      className="modal fade show d-block"
                      tabIndex="-1"
                      role="dialog"
                      aria-modal="true"
                      onClick={closeOpenBetsModal}
                    >
                      <div
                        className="modal-dialog modal-dialog-centered modal-lg"
                        role="document"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="modal-content">
                          <div className="modal-header">
                            <h5 className="modal-title text-uppercase">
                              Open Bets
                            </h5>
                            <button
                              type="button"
                              aria-label="Close"
                              className="btn"
                              style={{ marginLeft: "auto" }}
                              onClick={closeOpenBetsModal}
                            >
                              <span aria-hidden="true">X</span>
                            </button>
                          </div>
                          <div className="modal-body">
                            <PlaceBetOpenBets
                              matchId={matchId}
                              showPlaceBet={false}
                              showOpenBets
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : null}

                {/* Mobile/tablet: right column is hidden, so show TV here */}
                {matchTvUrl && !authUser?.is_demo ? (
                  <div className="mb-2 d-lg-none mt-2">
                    <button
                      type="button"
                      className="w-100 btn btn-sm btn-dark d-flex justify-content-between align-items-center"
                      onClick={() => setTvOpen((v) => !v)}
                    >
                      <span className="text-uppercase">Live TV</span>
                      <span
                        style={{
                          display: "inline-block",
                          transform: tvOpen ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 200ms ease",
                        }}
                        aria-hidden="true"
                      >
                        ▼
                      </span>
                    </button>
                    {tvOpen ? (
                      <div
                        className="mt-1 border rounded bg-white overflow-hidden w-full"
                        style={{
                          width: "100%",
                          maxHeight: 400,
                        }}
                      >
                        <iframe
                          title="Live TV"
                          src={matchTvUrl}
                          allow="autoplay; fullscreen; picture-in-picture"
                          allowFullScreen
                          style={{
                            width: "100%",
                            height: "100%",
                            border: 0,
                            display: "block",
                          }}
                        />
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {scoreboardUrl ? (
                  <div className="mb-2">
                    <button
                      type="button"
                      className="w-100 btn btn-sm btn-dark d-flex justify-content-between align-items-center"
                      onClick={() => setScoreCardOpen((v) => !v)}
                    >
                      <span className="text-uppercase">Scoreboard</span>
                      <span
                        style={{
                          display: "inline-block",
                          transform: scoreCardOpen
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                          transition: "transform 200ms ease",
                        }}
                        aria-hidden="true"
                      >
                        ▼
                      </span>
                    </button>
                    {scoreCardOpen ? (
                      <div
                        className="mt-1 border rounded bg-white overflow-hidden w-full"
                        style={{
                          width: "100%",
                          maxHeight: 400,
                        }}
                      >
                        <iframe
                          title="Live Scoreboard"
                          src={scoreboardUrl}
                          style={{
                            width: "100%",
                            height: "100%",
                            border: 0,
                            display: "block",
                          }}
                        />
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {/* Open Bets now opens via the header "Bets" modal on mobile */}
                <div>
                  {matchDetailsArray.map((market, index) => {
                    const runners = buildRunnersFromMarket(market);
                    if (!runners.length) return null;

                    const title =
                      market?.market_name ||
                      market?.name ||
                      `Market ${index + 1}`;
                    const minMaxText = getMarketMinMaxText(market);
                    const minStake = market?.inplay
                      ? market?.market_min_stack
                      : (market?.market_advance_bet_min_stake ?? null);
                    const maxStake = market?.inplay
                      ? market?.market_max_stack
                      : (market?.market_advance_bet_stake ?? null);
                    const collapseId =
                      market?.market_id || market?.id || `market-${index}`;

                    const marketKey =
                      market?.market_id ||
                      market?.id ||
                      market?.marketId ||
                      null;

                    const marketPositions =
                      teamPosition?.[marketKey] ??
                      teamPosition?.[String(marketKey)] ??
                      [];
                    const marketId =
                      market?.market_id ||
                      market?.id ||
                      market?.marketId ||
                      null;
                    const initiallyBookmarked = isMarkedFavorite(
                      matchId,
                      marketId,
                    );
                    const hasTeamPositionForMarket =
                      !!marketKey &&
                      teamPosition &&
                      typeof teamPosition === "object" &&
                      Array.isArray(
                        teamPosition[marketKey] ||
                          teamPosition[String(marketKey)],
                      ) &&
                      (
                        teamPosition[marketKey] ||
                        teamPosition[String(marketKey)]
                      ).length > 0;
                    const isCashoutSupportedMarket = Array.isArray(
                      market?.runners,
                    );

                    //this is correct way to check if cashout is supported but for now iam letting it to be true always
                    // const isCashoutSupportedMarket =
                    //   Array.isArray(market?.runners) && market.runners.length === 2;

                    const typeNorm = normalizeMarketType(market?.market_type);
                    const nameNorm = normalizeMarketType(
                      market?.market_name || market?.name,
                    );

                    const isBookmaker =
                      typeNorm.includes("BOOKMAKER") ||
                      nameNorm.includes("BOOKMAKER");
                    const isToss =
                      typeNorm.includes("TOSS") || nameNorm.includes("TOSS");

                    return (
                      <MatchOddsSection
                        key={collapseId}
                        title={title}
                        market={market}
                        minMaxText={minMaxText}
                        minStake={minStake}
                        maxStake={maxStake}
                        collapseId={String(collapseId)}
                        marketPositions={marketPositions}
                        runners={runners}
                        showCashout={
                          hasTeamPositionForMarket && isCashoutSupportedMarket
                        }
                        showMarquee={isBookmaker}
                        onOddsClick={dispatchSelectedBet}
                        matchId={matchId}
                        marketId={marketId}
                        initialBookmarked={initiallyBookmarked}
                        marqueeText={
                          isBookmaker
                            ? "England V India : Advance BM ,Toss & Fancy Bets Started🏏"
                            : ""
                        }
                      />
                    );
                  })}
                  {/* Static combo / win sections can be wired to dynamic data later */}
                  {/* {comboAndWinSections.map((section) => (
                    <ComboOrWinSection key={section.collapseId} section={section} />
                  ))} */}
                </div>
              </>
            )}
            {/* Fancy markets from API */}
            {!matchLoading && (
              <div className="newtab_collect tab-container mt-3">
                <div className="fancy-heading-bar">
                  <span>Fancy</span>
                </div>
                <ul
                  role="tablist"
                  className="nav nav-tabs fancy-subtabs"
                  aria-label="Tabs"
                >
                  {fancyTabs.map((tab) => {
                    const isActive = activeFancyTab === tab;
                    return (
                      <li
                        key={tab}
                        className={`nav-item${isActive ? " active" : ""}`}
                      >
                        <a
                          href="#/"
                          role="tab"
                          className={`nav-link${isActive ? " active" : ""}`}
                          aria-selected={isActive ? "true" : "false"}
                          onClick={(e) => {
                            e.preventDefault();
                            setActiveFancyTab(tab);
                          }}
                        >
                          <span>{tab}</span>
                        </a>
                      </li>
                    );
                  })}
                </ul>
                <div className="tab-content">
                  <div className="tab-pane active">
                    <div className="market_section twoOddsSection">
                      <div className="fancy-tab">
                        <div className="tab-container">
                          <div className="fancy-tab-content">
                            {filteredFancyItems.length === 0 ? (
                              <div className="row align-items-center oddsHeader">
                                <div className="col-12 py-3 text-center">
                                  No fancy markets
                                </div>
                              </div>
                            ) : (
                              <>
                                <div className="row align-items-center oddsHeader">
                                  <div className="col-lg-7 col-md-7 col-7 px-0" />
                                  <div className="col-lg-3 col-md-3 col-5 px-0">
                                    <div className="btn-group">
                                      <button className="w-100 lay">no</button>
                                      <button className="w-100 back">
                                        yes
                                      </button>
                                    </div>
                                  </div>
                                  <div className="col-lg-2 col-md-2 col-4 px-0 d-none d-md-block" />
                                </div>
                                {filteredFancyItems.map((item) => (
                                  <FancyDetailRow
                                    key={item.id}
                                    item={item}
                                    onOddsClick={dispatchSelectedBet}
                                    matchId={matchId}
                                    marketId={marketId}
                                  />
                                ))}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="col-md-12 col-lg-4 detail-right-column d-none d-lg-block px-0">
          {/* Desktop: show Live TV above betslip */}
          {matchTvUrl ? (
            <div className="mb-2">
              <button
                type="button"
                className="w-100 btn btn-sm btn-dark d-flex justify-content-between align-items-center"
                onClick={() => setTvOpen((v) => !v)}
              >
                <span className="text-uppercase">Live TV</span>
                <span
                  style={{
                    display: "inline-block",
                    transform: tvOpen ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 200ms ease",
                  }}
                  aria-hidden="true"
                >
                  ▼
                </span>
              </button>
              {tvOpen ? (
                <div
                  className="mt-1 border rounded bg-white overflow-hidden w-full"
                  style={{
                    width: "100%",
                    maxHeight: 400,
                  }}
                >
                  <iframe
                    title="Live TV"
                    src={matchTvUrl}
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    style={{
                      width: "100%",
                      height: "100%",
                      border: 0,
                      display: "block",
                    }}
                  />
                </div>
              ) : null}
            </div>
          ) : null}
          <PlaceBetOpenBets matchId={matchId} />
        </div>
      </div>
      {legacyFancyAndCombo}
    </section>
  );
};

export default SportDetail;
